/* eslint-disable max-lines */
import {GRANITE_1, GRANITE_3} from '@gaia/colors';
import Button from '@gaia/components/Button';
import {recaptchaActionForLogin, useReCaptcha} from '@gaia/hooks/useRecaptcha';
import {gaiaOutside} from '@gaia/images/logos';
import {useLoadableUserIsAuthenticated, useRefreshUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import type {AuthFormProps} from '@gaia/types/AuthFormProps';
import gettext from '@gaia/utils/django/gettext';
import fetchJSON from '@gaia/utils/fetchJSON';
import getCSRFToken from '@gaia/utils/gaia/getCSRFToken';
import FacebookIcon from '@mui/icons-material/Facebook';
import {
    Button as MuiButton,
    CircularProgress,
    Divider,
    FormHelperText,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import qs from 'qs';
import {type FormEvent, useCallback, useReducer, useState} from 'react';
import {GoogleReCaptcha} from 'react-google-recaptcha-v3';

import Alert from './Alert';
import LoginFormContainer from './LoginFormContainer';

type LoginResponseCode = 1000 | 1080;

interface LoginResponse {
    code: LoginResponseCode;
}

interface LoggedOutState {
    type: 'loggedOut';
    error?: {
        code: number;
        message: string;
    };
}

interface LoggingInState {
    type: 'loggingIn';
}

interface AuthedState {
    type: 'authed';
}

interface LoggingInAction {
    type: 'loggingIn';
}

interface AuthAction {
    type: 'authed';
}

interface ErrorAction {
    type: 'error';
    error: {
        code: LoginResponseCode | -1; // -1 is a catch-all for unknown errors
        message: string;
    };
}

type LoginState = LoggedOutState | LoggingInState | AuthedState;
type LoginAction = LoggingInAction | ErrorAction | AuthAction;

/*  State diagram:
 *
 *       +-----------------------▶ authed ▶-----------------------+
 *       |                                                        |
 *       ▲                                                        ▼
 * +-----------+                 +-----------+                +--------+
 * | loggedOut | ▶- loggingIn -▶ | loggingIn | ▶-- authed --▶ | authed |
 * +-----------+                 +-----------+                +--------+
 *       ▲                            ▼
 *       |                            |
 *       +----------- error ----------+
 */

function loginStateReducer(state: LoginState, action: LoginAction): LoginState {
    if (action.type === 'loggingIn') {
        if (state.type === 'loggedOut') return {type: 'loggingIn'};
    } else if (action.type === 'error') {
        if (state.type === 'loggingIn') return {type: 'loggedOut', error: action.error};
    } else if (action.type === 'authed') {
        // can jump to authed state from any state
        return {type: 'authed'};
    }

    throw new Error(`Invalid action '${action.type}' for state '${state.type}'`);
}

export default function LoginForm({
    nextPage,
    setAuthModalType,
    onSubmitDone,
    onUserAuthenticated,
    analyticsContext,
}: AuthFormProps) {
    const isAuthenticated = useLoadableUserIsAuthenticated();

    const [loginState, dispatch] = useReducer(
        loginStateReducer,
        isAuthenticated ? {type: 'authed'} : {type: 'loggedOut'}
    );

    const refreshUser = useRefreshUser();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const {recaptcha, lastRecaptchaRefresh, triggerRecaptchaRefresh, handleRecaptchaVerify} =
        useReCaptcha();

    // if the `setAuthModalType` cb prop is provided, call it and 🤞 that it handles the
    // form type change to `"register"`; otherwise redirect to `/register/`
    const onRegisterClick = useCallback(() => {
        if (setAuthModalType) setAuthModalType('register');
        else window.location.href = `/register/?next=${nextPage}`;
    }, [setAuthModalType, nextPage]);

    const isErrored = loginState.type === 'loggedOut' && loginState.error !== undefined;

    const isLoginDisabled = loginState.type !== 'loggedOut';

    function onSubmit(event: FormEvent) {
        event.preventDefault();

        if (loginState.type !== 'loggedOut') return;

        dispatch({type: 'loggingIn'});

        const csrf = getCSRFToken();

        fetchJSON('/register/addDevice/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                ...(csrf && {'X-CSRFToken': csrf}),
                Accept: 'application/json',
            },
            body: qs.stringify({
                email,
                password,
                analytics_context: analyticsContext,
                recaptcha,
            }),
        })
            .then(({code}: LoginResponse) => {
                switch (code) {
                    case 1000:
                        refreshUser();
                        // `if (isAuthenticated) ...` above will also trigger state change after
                        // `user` is refreshed; this call is proactive
                        dispatch({type: 'authed'});
                        onUserAuthenticated?.();
                        break;
                    case 1080:
                        dispatch({
                            type: 'error',
                            error: {
                                code,
                                message: gettext('Invalid email or password.'),
                            },
                        });
                        break;
                    default:
                        throw new Error(`Invalid response code (${code})`);
                }
                // Eagerly refresh recaptcha token on submit.
                triggerRecaptchaRefresh();
            })
            .catch((err: Error) => {
                dispatch({
                    type: 'error',
                    error: {
                        code: -1,
                        message: err.message ?? 'Unknown error',
                    },
                });
            })
            .finally(() => onSubmitDone?.());
    }

    return (
        <LoginFormContainer
            panelPrimary={panelPrimary}
            panelSecondary={
                <div>
                    <GoogleReCaptcha
                        action={recaptchaActionForLogin}
                        onVerify={handleRecaptchaVerify}
                        refreshReCaptcha={lastRecaptchaRefresh}
                    />
                    <Typography
                        variant='h4'
                        style={{
                            fontSize: 24,
                            fontWeight: 'bold',
                            color: GRANITE_1,
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        {gettext('Log In')}
                    </Typography>

                    <form autoComplete='off' autoCapitalize='off' onSubmit={onSubmit}>
                        <TextField
                            id='login-email'
                            style={{
                                display: 'block',
                                marginBottom: theme.spacing(2),
                            }}
                            fullWidth
                            label={gettext('Email')}
                            type='text' // this is 'text' rather than 'email' to allow username-only logins
                            variant='outlined'
                            required
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            error={isErrored}
                            InputLabelProps={{
                                required: false,
                                style: {fontSize: 14},
                            }}
                        />
                        <TextField
                            id='login-password'
                            style={{
                                display: 'block',
                            }}
                            fullWidth
                            label={gettext('Password')}
                            type='password'
                            variant='outlined'
                            required
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            error={isErrored}
                            InputLabelProps={{
                                required: false,
                                style: {fontSize: 14},
                            }}
                        />

                        <FormHelperText
                            variant='filled'
                            style={{
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            <Link
                                href='/user/password/reset/?next=/login/'
                                style={{
                                    fontSize: 12,
                                    color: GRANITE_3,
                                }}
                                target='_blank'
                                rel='noopener'
                            >
                                {gettext('Forgot your password?')}
                            </Link>
                        </FormHelperText>

                        {isErrored && (
                            <Alert
                                variant='error'
                                style={{
                                    marginBottom: theme.spacing(2),
                                }}
                            >
                                <Typography
                                    variant='body1'
                                    id='unknown-error'
                                    style={{
                                        fontSize: 14,
                                    }}
                                >
                                    {loginState.error?.code === 1080
                                        ? 'Incorrect email or password. If you have an Outside account, please log in with your Outside email and password.'
                                        : 'An unknown error occurred. Please try again.'}
                                </Typography>
                            </Alert>
                        )}

                        <Button
                            appearance='primary'
                            type='submit'
                            size='large'
                            fullWidth
                            disabled={isLoginDisabled}
                        >
                            {loginState.type === 'loggingIn' ? (
                                <CircularProgress thickness={7} size={24} disableShrink />
                            ) : (
                                gettext('Log In')
                            )}
                        </Button>
                    </form>

                    {or}

                    <MuiButton
                        component='a'
                        variant='contained'
                        fullWidth
                        size='large'
                        style={{
                            backgroundColor: '#3b5998',
                            color: '#fff',
                        }}
                        startIcon={<FacebookIcon />}
                        href={`/login/facebook/?next=${nextPage}`}
                        disabled={isLoginDisabled}
                        target='_blank'
                        rel='noopener'
                    >
                        {gettext('Log In with Facebook')}
                    </MuiButton>

                    <Divider
                        style={{
                            marginTop: theme.spacing(3),
                            marginBottom: theme.spacing(2),
                            width: '100%',
                        }}
                    />

                    <Typography variant='body2' style={{fontSize: 14, color: GRANITE_3}}>
                        {`Don't have a Gaia GPS account? `}
                        <Link onClick={onRegisterClick}>Sign up!</Link>
                    </Typography>
                </div>
            }
        />
    );
}

const panelPrimary = (
    <div>
        <img
            src={gaiaOutside}
            alt='Gaia GPS from Outside'
            style={{marginBottom: theme.spacing(2)}}
            height='49'
            width='200'
        />
        <Typography variant='body2' style={{fontSize: 14, marginBottom: theme.spacing(2)}}>
            Gaia GPS is part of the Outside family. Outside members can use their existing email and
            password to log in to Gaia GPS.
        </Typography>
        <Link
            color='inherit'
            style={{fontSize: 14}}
            href='/outsideplus/'
            target='_blank'
            rel='noopener noreferrer'
        >
            Learn more
        </Link>
    </div>
);

const or = (
    <div
        style={{
            textAlign: 'center',
            borderTop: '1px solid',
            borderTopColor: theme.palette.divider,
            height: 1,
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(3),
        }}
    >
        <div
            style={{
                padding: '0 12px',
                color: GRANITE_3,
                backgroundColor: '#fff',
                display: 'inline-block',
                transform: 'translateY(-77%)',
                fontSize: 12,
            }}
        >
            or
        </div>
    </div>
);
